import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Loader2, Plus, Home, Edit, Trash2, Users, Bell, Calendar as CalendarIcon, Upload, X } from "lucide-react";
import { PropertyImageGallery } from "@/components/PropertyImageGallery";
import { PlacementCalendar } from "@/components/PlacementCalendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";

interface Property {
  id: string;
  property_type?: string;
  name: string;
  address: string;
  unit?: string;
  city: string;
  state: string;
  zip: string;
  bedrooms: number;
  bathrooms: number;
  square_footage?: number;
  minimum_lease_months?: number;
  monthly_rent?: number;
  security_deposit?: number;
  rent_amount: number;
  date_available?: string;
  property_website?: string;
  status: string;
  description: string;
  amenities?: string;
  exterior_fenced_yard?: boolean;
  exterior_single_story?: boolean;
  exterior_ada_compliant?: boolean;
  exterior_balcony_deck?: boolean;
  exterior_finished_basement?: boolean;
  exterior_swimming_pool?: boolean;
  exterior_hot_tub?: boolean;
  appliance_stove?: boolean;
  appliance_oven?: boolean;
  appliance_microwave?: boolean;
  appliance_dishwasher?: boolean;
  appliance_refrigerator?: boolean;
  appliance_washing_machine?: boolean;
  appliance_clothes_dryer?: boolean;
  appliance_central_air?: boolean;
  flooring_carpet?: boolean | null;
  flooring_hardwood?: boolean | null;
  flooring_vinyl?: boolean | null;
  flooring_other?: boolean | null;
  windows_blinds?: boolean | null;
  windows_drapes?: boolean | null;
  windows_shades?: boolean | null;
  windows_none?: boolean | null;
  utility_landscaping?: boolean;
  utility_lawn_care?: boolean;
  utility_snow_removal?: boolean;
  utility_electric?: boolean;
  utility_gas?: boolean;
  utility_water?: boolean;
  utility_oil?: boolean;
  utility_cable_internet?: boolean;
  utility_renters_insurance?: boolean;
  parking_garage?: boolean | null;
  parking_carport?: boolean | null;
  parking_street?: boolean | null;
  parking_reserved_spot?: boolean | null;
  pets_cats_allowed?: boolean | null;
  pets_dogs_allowed?: boolean | null;
  pets_other_allowed?: boolean | null;
  pets_deposit?: number | null;
  pets_fee?: number | null;
  pets_max_dogs?: number | null;
  pets_max_cats?: number | null;
  created_at: string;
}

interface Placement {
  id: string;
  client_id: string;
  property_id: string;
  start_date: string;
  end_date: string | null;
  status: string;
  notes: string | null;
  properties: {
    name: string;
  };
}

const LandlordDashboard = () => {
  const navigate = useNavigate();
  const { impersonatedUser, isImpersonating } = useImpersonation();
  const [user, setUser] = useState<User | null>(null);
  const [userFullName, setUserFullName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState<Property[]>([]);
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);
  const [selectedPropertyForImages, setSelectedPropertyForImages] = useState<string | null>(null);
  const [showImagesDialog, setShowImagesDialog] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    property_type: "",
    name: "",
    address: "",
    unit: "",
    city: "",
    state: "",
    zip: "",
    bedrooms: 1,
    bathrooms: 1,
    square_footage: 0,
    minimum_lease_months: 0,
    monthly_rent: 0,
    security_deposit: 0,
    rent_amount: 0,
    date_available: undefined as Date | undefined,
    property_website: "",
    status: "available",
    description: "",
    amenities: "",
    exterior_fenced_yard: false,
    exterior_single_story: false,
    exterior_ada_compliant: false,
    exterior_balcony_deck: false,
    exterior_finished_basement: false,
    exterior_swimming_pool: false,
    exterior_hot_tub: false,
    appliance_stove: false,
    appliance_oven: false,
    appliance_microwave: false,
    appliance_dishwasher: false,
    appliance_refrigerator: false,
    appliance_washing_machine: false,
    appliance_clothes_dryer: false,
    appliance_central_air: false,
    flooring_carpet: false,
    flooring_hardwood: false,
    flooring_vinyl: false,
    flooring_other: false,
    windows_blinds: false,
    windows_drapes: false,
    windows_shades: false,
    windows_none: false,
    utility_landscaping: false,
    utility_lawn_care: false,
    utility_snow_removal: false,
    utility_electric: false,
    utility_gas: false,
    utility_water: false,
    utility_oil: false,
    utility_cable_internet: false,
    utility_renters_insurance: false,
    parking_garage: false,
    parking_carport: false,
    parking_street: false,
    parking_reserved_spot: false,
    pets_cats_allowed: false,
    pets_dogs_allowed: false,
    pets_other_allowed: false,
    pets_deposit: 0,
    pets_fee: 0,
    pets_max_dogs: 0,
    pets_max_cats: 0
  });

  const effectiveUserId = isImpersonating ? impersonatedUser?.id : user?.id;

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (effectiveUserId) {
      fetchProperties();
      fetchPlacements();
    }
  }, [effectiveUserId]);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }

      setUser(session.user);

      // Fetch profile name for the effective user
      const nameUserId = isImpersonating ? impersonatedUser?.id : session.user.id;
      const { data: profileData } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("id", nameUserId!)
        .maybeSingle();

      if (profileData) {
        const fullName = [profileData.first_name, profileData.last_name].filter(Boolean).join(" ");
        setUserFullName(fullName || (isImpersonating ? impersonatedUser?.email : session.user.email) || "Unknown");
      } else {
        setUserFullName((isImpersonating ? impersonatedUser?.email : session.user.email) || "Unknown");
      }

      if (!isImpersonating) {
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .eq("role", "landlord")
          .maybeSingle();

        if (!roleData) {
          toast.error("Access denied. Landlord privileges required.");
          navigate("/dashboard");
          return;
        }
      }
    } catch (error: any) {
      console.error("Error checking auth:", error);
      toast.error("Authentication error");
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      // Check if the real user is an admin
      const { data: { session } } = await supabase.auth.getSession();
      const realUserId = session?.user?.id;
      let isAdmin = false;
      if (realUserId) {
        const { data: adminRole } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", realUserId)
          .eq("role", "admin")
          .maybeSingle();
        isAdmin = !!adminRole;
      }

      let query = supabase
        .from("properties")
        .select("*")
        .order("created_at", { ascending: false });

      // Non-admin landlords only see their own properties
      if (!isAdmin && effectiveUserId) {
        query = query.eq("landlord_id", effectiveUserId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setProperties(data || []);
    } catch (error: any) {
      console.error("Error fetching properties:", error);
      toast.error("Failed to load properties");
    }
  };

  const fetchPlacements = async () => {
    try {
      const { data, error } = await supabase
        .from("placements")
        .select(`
          *,
          properties(name)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPlacements(data || []);
    } catch (error: any) {
      console.error("Error fetching placements:", error);
      toast.error("Failed to load placements");
    }
  };

  const handleSubmit = async () => {
    if (!user) return;

    try {
      const submitData = {
        ...formData,
        rent_amount: formData.monthly_rent || formData.rent_amount || 0,
        date_available: formData.date_available ? format(formData.date_available, "yyyy-MM-dd") : null
      };

      let propertyId: string;

      if (editingProperty) {
        const { error } = await supabase
          .from("properties")
          .update({
            ...submitData,
            submitted_by: userFullName,
            submitted_at: new Date().toISOString()
          })
          .eq("id", editingProperty.id);

        if (error) throw error;
        propertyId = editingProperty.id;
        toast.success("Property updated successfully");
      } else {
        const { data, error } = await supabase
          .from("properties")
          .insert({
            ...submitData,
            landlord_id: effectiveUserId!,
            status: 'pending',
            submitted_by: userFullName,
            submitted_at: new Date().toISOString()
          })
          .select()
          .single();

        if (error) throw error;
        propertyId = data.id;
        toast.success("Property submitted for approval");
      }

      // Upload images if any
      if (uploadedImages.length > 0) {
        await uploadPropertyImages(propertyId);
      }

      setShowAddDialog(false);
      setEditingProperty(null);
      resetForm();
      setUploadedImages([]);
      setImagePreviewUrls([]);
      fetchProperties();
    } catch (error: any) {
      console.error("Error saving property:", error);
      toast.error(error.message || "Failed to save property");
    }
  };

  const uploadPropertyImages = async (propertyId: string) => {
    try {
      for (let i = 0; i < uploadedImages.length; i++) {
        const file = uploadedImages[i];
        const fileExt = file.name.split('.').pop();
        const fileName = `${propertyId}-${Date.now()}-${i}.${fileExt}`;
        const filePath = `${propertyId}/${fileName}`;

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from('property-images')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        // Save reference to database
        const { error: dbError } = await supabase
          .from('property_images')
          .insert({
            property_id: propertyId,
            file_name: fileName,
            file_path: filePath,
            display_order: i,
            is_primary: i === 0
          });

        if (dbError) throw dbError;
      }
      toast.success("Images uploaded successfully");
    } catch (error: any) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload some images");
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remainingSlots = 3 - uploadedImages.length;
    
    if (files.length > remainingSlots) {
      toast.error(`You can only upload ${remainingSlots} more image(s)`);
      return;
    }

    // Validate file types
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        toast.error(`${file.name} is not a valid image file`);
      }
      return isImage;
    });

    if (validFiles.length === 0) return;

    // Create preview URLs
    const newPreviewUrls = validFiles.map(file => URL.createObjectURL(file));
    
    setUploadedImages(prev => [...prev, ...validFiles]);
    setImagePreviewUrls(prev => [...prev, ...newPreviewUrls]);
  };

  const removeImage = (index: number) => {
    // Revoke the preview URL to free memory
    URL.revokeObjectURL(imagePreviewUrls[index]);
    
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (property: Property) => {
    setEditingProperty(property);
    setUploadedImages([]);
    setImagePreviewUrls([]);
    setFormData({
      property_type: property.property_type || "",
      name: property.name,
      address: property.address,
      unit: property.unit || "",
      city: property.city,
      state: property.state,
      zip: property.zip,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      square_footage: property.square_footage || 0,
      minimum_lease_months: property.minimum_lease_months || 0,
      monthly_rent: property.monthly_rent || 0,
      security_deposit: property.security_deposit || 0,
      rent_amount: property.rent_amount,
      date_available: property.date_available ? new Date(property.date_available) : undefined,
      property_website: property.property_website || "",
      status: property.status,
      description: property.description,
      amenities: property.amenities || "",
      exterior_fenced_yard: property.exterior_fenced_yard || false,
      exterior_single_story: property.exterior_single_story || false,
      exterior_ada_compliant: property.exterior_ada_compliant || false,
      exterior_balcony_deck: property.exterior_balcony_deck || false,
      exterior_finished_basement: property.exterior_finished_basement || false,
      exterior_swimming_pool: property.exterior_swimming_pool || false,
      exterior_hot_tub: property.exterior_hot_tub || false,
      appliance_stove: property.appliance_stove || false,
      appliance_oven: property.appliance_oven || false,
      appliance_microwave: property.appliance_microwave || false,
      appliance_dishwasher: property.appliance_dishwasher || false,
      appliance_refrigerator: property.appliance_refrigerator || false,
      appliance_washing_machine: property.appliance_washing_machine || false,
      appliance_clothes_dryer: property.appliance_clothes_dryer || false,
      appliance_central_air: property.appliance_central_air || false,
      flooring_carpet: property.flooring_carpet || false,
      flooring_hardwood: property.flooring_hardwood || false,
      flooring_vinyl: property.flooring_vinyl || false,
      flooring_other: property.flooring_other || false,
      windows_blinds: property.windows_blinds || false,
      windows_drapes: property.windows_drapes || false,
      windows_shades: property.windows_shades || false,
      windows_none: property.windows_none || false,
      utility_landscaping: property.utility_landscaping || false,
      utility_lawn_care: property.utility_lawn_care || false,
      utility_snow_removal: property.utility_snow_removal || false,
      utility_electric: property.utility_electric || false,
      utility_gas: property.utility_gas || false,
      utility_water: property.utility_water || false,
      utility_oil: property.utility_oil || false,
      utility_cable_internet: property.utility_cable_internet || false,
      utility_renters_insurance: property.utility_renters_insurance || false,
      parking_garage: property.parking_garage || false,
      parking_carport: property.parking_carport || false,
      parking_street: property.parking_street || false,
      parking_reserved_spot: property.parking_reserved_spot || false,
      pets_cats_allowed: property.pets_cats_allowed || false,
      pets_dogs_allowed: property.pets_dogs_allowed || false,
      pets_other_allowed: property.pets_other_allowed || false,
      pets_deposit: property.pets_deposit || 0,
      pets_fee: property.pets_fee || 0,
      pets_max_dogs: property.pets_max_dogs || 0,
      pets_max_cats: property.pets_max_cats || 0
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this property?")) return;

    try {
      const { error } = await supabase
        .from("properties")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("Property deleted successfully");
      fetchProperties();
    } catch (error: any) {
      console.error("Error deleting property:", error);
      toast.error("Failed to delete property");
    }
  };

  const resetForm = () => {
    setFormData({
      property_type: "",
      name: "",
      address: "",
      unit: "",
      city: "",
      state: "",
      zip: "",
      bedrooms: 1,
      bathrooms: 1,
      square_footage: 0,
      minimum_lease_months: 0,
      monthly_rent: 0,
      security_deposit: 0,
      rent_amount: 0,
      date_available: undefined,
      property_website: "",
      status: "pending",
      description: "",
      amenities: "",
      exterior_fenced_yard: false,
      exterior_single_story: false,
      exterior_ada_compliant: false,
      exterior_balcony_deck: false,
      exterior_finished_basement: false,
      exterior_swimming_pool: false,
      exterior_hot_tub: false,
      appliance_stove: false,
      appliance_oven: false,
      appliance_microwave: false,
      appliance_dishwasher: false,
      appliance_refrigerator: false,
      appliance_washing_machine: false,
      appliance_clothes_dryer: false,
      appliance_central_air: false,
      flooring_carpet: false,
      flooring_hardwood: false,
      flooring_vinyl: false,
      flooring_other: false,
      windows_blinds: false,
      windows_drapes: false,
      windows_shades: false,
      windows_none: false,
      utility_landscaping: false,
      utility_lawn_care: false,
      utility_snow_removal: false,
      utility_electric: false,
      utility_gas: false,
      utility_water: false,
      utility_oil: false,
      utility_cable_internet: false,
      utility_renters_insurance: false,
      parking_garage: false,
      parking_carport: false,
      parking_street: false,
      parking_reserved_spot: false,
      pets_cats_allowed: false,
      pets_dogs_allowed: false,
      pets_other_allowed: false,
      pets_deposit: 0,
      pets_fee: 0,
      pets_max_dogs: 0,
      pets_max_cats: 0
    });
  };

  const handlePlacementStatusChange = async (placementId: string, currentStatus: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from("placements")
        .update({ status: newStatus })
        .eq("id", placementId);

      if (error) throw error;

      // Send notification email
      try {
        await supabase.functions.invoke("send-placement-notification", {
          body: {
            placementId,
            oldStatus: currentStatus,
            newStatus
          }
        });
        toast.success(`Placement status updated to ${newStatus}. Client notified via email.`);
      } catch (emailError) {
        console.error("Failed to send notification email:", emailError);
        toast.success(`Placement status updated to ${newStatus}.`);
      }

      fetchPlacements();
    } catch (error: any) {
      console.error("Error updating placement status:", error);
      toast.error("Failed to update placement status");
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
      pending: "outline",
      approved: "default",
      available: "default",
      occupied: "secondary",
      rejected: "destructive"
    };
    return variants[status] || "outline";
  };

  const getPlacementStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "outline"> = {
      pending: "outline",
      active: "default",
      completed: "secondary"
    };
    return variants[status] || "outline";
  };

  const getPlacementStatusLabel = (status: string) => {
    if (status === "pending") return "Pending";
    if (status === "completed") return "Closed";
    return "Active";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-1 container mx-auto px-4 py-8 mt-20">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Stats Overview */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Total Properties</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{properties.length}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {properties.filter(p => p.status === "available").length} available
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Active Placements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {placements.filter(p => p.status === "active").length}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Currently occupied
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Pending Placements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {placements.filter(p => p.status === "pending").length}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Awaiting approval
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Properties</h2>
              <p className="text-muted-foreground mt-1">
                Manage your property portfolio
              </p>
            </div>
            
            <Dialog open={showAddDialog} onOpenChange={(open) => {
              setShowAddDialog(open);
              if (!open) {
                setEditingProperty(null);
                resetForm();
                setUploadedImages([]);
                setImagePreviewUrls([]);
              }
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Property
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingProperty ? "Edit Property" : "Add New Property"}
                  </DialogTitle>
                  <DialogDescription>
                    {editingProperty ? "Update property details" : "Add a new property to your portfolio"}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="property_type">Property Type</Label>
                    <Select
                      value={formData.property_type}
                      onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                    >
                      <SelectTrigger id="property_type">
                        <SelectValue placeholder="Select property type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single_family">Single Family Home</SelectItem>
                        <SelectItem value="apartment">Apartment</SelectItem>
                        <SelectItem value="condo">Condo</SelectItem>
                        <SelectItem value="townhouse">Townhouse</SelectItem>
                        <SelectItem value="duplex">Duplex</SelectItem>
                        <SelectItem value="mobile_home">Mobile Home</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="name">Property Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Sunset Apartments Unit 2A"
                      className="placeholder:text-muted-foreground"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="123 Main St"
                        className="placeholder:text-muted-foreground"
                      />
                    </div>
                    <div>
                      <Label htmlFor="unit">Unit</Label>
                      <Input
                        id="unit"
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        placeholder="Apt 2A"
                        className="placeholder:text-muted-foreground"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip">ZIP</Label>
                      <Input
                        id="zip"
                        value={formData.zip}
                        onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <h3 className="text-lg font-semibold mb-4">Property Details</h3>
                  </div>
                  
                  <div>
                    <Label htmlFor="date_available">Date Available</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !formData.date_available && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.date_available ? format(formData.date_available, "PPP") : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.date_available}
                          onSelect={(date) => setFormData({ ...formData, date_available: date })}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="bedrooms">Bedrooms</Label>
                      <Input
                        id="bedrooms"
                        type="number"
                        min="0"
                        value={formData.bedrooms}
                        onChange={(e) => setFormData({ ...formData, bedrooms: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="bathrooms">Bathrooms</Label>
                      <Input
                        id="bathrooms"
                        type="number"
                        step="0.5"
                        min="0"
                        value={formData.bathrooms}
                        onChange={(e) => setFormData({ ...formData, bathrooms: parseFloat(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="square_footage">Square Footage</Label>
                      <Input
                        id="square_footage"
                        type="number"
                        min="0"
                        value={formData.square_footage}
                        onChange={(e) => setFormData({ ...formData, square_footage: parseFloat(e.target.value) })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="minimum_lease_months">Minimum Lease Considered</Label>
                      <Select
                        value={formData.minimum_lease_months?.toString() || ""}
                        onValueChange={(value) => setFormData({ ...formData, minimum_lease_months: parseInt(value) })}
                      >
                        <SelectTrigger id="minimum_lease_months">
                          <SelectValue placeholder="Select months" />
                        </SelectTrigger>
                        <SelectContent className="bg-background">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => (
                            <SelectItem key={month} value={month.toString()}>
                              {month} {month === 1 ? 'month' : 'months'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="monthly_rent">Monthly Rent</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          id="monthly_rent"
                          type="text"
                          inputMode="decimal"
                          value={formData.monthly_rent}
                          onChange={(e) => setFormData({ ...formData, monthly_rent: parseFloat(e.target.value) || 0 })}
                          className="pl-7"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="security_deposit">Security Deposit Amount</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          id="security_deposit"
                          type="text"
                          inputMode="decimal"
                          value={formData.security_deposit}
                          onChange={(e) => setFormData({ ...formData, security_deposit: parseFloat(e.target.value) || 0 })}
                          className="pl-7"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="property_website">Property Website Link</Label>
                    <Input
                      id="property_website"
                      type="url"
                      value={formData.property_website}
                      onChange={(e) => setFormData({ ...formData, property_website: e.target.value })}
                      placeholder="https://example.com"
                    />
                  </div>
                  
                  <div>
                    <Label>Property Photos (Max 3)</Label>
                    <div className="space-y-4">
                      {imagePreviewUrls.length < 3 && (
                        <div>
                          <Input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageSelect}
                            className="cursor-pointer"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {uploadedImages.length} of 3 images uploaded
                          </p>
                        </div>
                      )}
                      
                      {imagePreviewUrls.length > 0 && (
                        <div className="grid grid-cols-3 gap-4">
                          {imagePreviewUrls.map((url, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={url}
                                alt={`Preview ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg border"
                              />
                              <Button
                                type="button"
                                variant="destructive"
                                size="icon"
                                className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => removeImage(index)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                              {index === 0 && (
                                <Badge className="absolute bottom-1 left-1 text-xs">Primary</Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Additional details about the property..."
                      rows={4}
                    />
                  </div>
                  
                  <div className="pt-4 border-t">
                    <h3 className="text-lg font-semibold mb-4">Property Specification and Amenities</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-muted-foreground mb-3">
                          Add specific property features and amenities to help attract tenants.
                        </p>
                        <Textarea
                          id="amenities"
                          value={formData.amenities}
                          onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                          placeholder="List amenities and specifications (e.g., Parking, Laundry, Dishwasher, Air Conditioning, Pet Friendly, etc.)"
                          rows={4}
                        />
                      </div>
                      
                      <div className="pt-3 border-t">
                        <h4 className="font-semibold mb-3">Exterior</h4>
                        <div className="grid grid-cols-2 grid-flow-col grid-rows-4 gap-3">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="exterior_fenced_yard"
                              checked={formData.exterior_fenced_yard}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, exterior_fenced_yard: checked as boolean })
                              }
                            />
                            <Label htmlFor="exterior_fenced_yard" className="font-normal cursor-pointer">
                              Fenced Yard
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="exterior_single_story"
                              checked={formData.exterior_single_story}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, exterior_single_story: checked as boolean })
                              }
                            />
                            <Label htmlFor="exterior_single_story" className="font-normal cursor-pointer">
                              Single Story
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="exterior_ada_compliant"
                              checked={formData.exterior_ada_compliant}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, exterior_ada_compliant: checked as boolean })
                              }
                            />
                            <Label htmlFor="exterior_ada_compliant" className="font-normal cursor-pointer">
                              ADA Compliant
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="exterior_balcony_deck"
                              checked={formData.exterior_balcony_deck}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, exterior_balcony_deck: checked as boolean })
                              }
                            />
                            <Label htmlFor="exterior_balcony_deck" className="font-normal cursor-pointer">
                              Balcony/Deck
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="exterior_finished_basement"
                              checked={formData.exterior_finished_basement}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, exterior_finished_basement: checked as boolean })
                              }
                            />
                            <Label htmlFor="exterior_finished_basement" className="font-normal cursor-pointer">
                              Finished Basement
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="exterior_swimming_pool"
                              checked={formData.exterior_swimming_pool}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, exterior_swimming_pool: checked as boolean })
                              }
                            />
                            <Label htmlFor="exterior_swimming_pool" className="font-normal cursor-pointer">
                              Swimming Pool
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="exterior_hot_tub"
                              checked={formData.exterior_hot_tub}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, exterior_hot_tub: checked as boolean })
                              }
                            />
                            <Label htmlFor="exterior_hot_tub" className="font-normal cursor-pointer">
                              Hot Tub
                            </Label>
                          </div>
                        </div>
                      </div>
                      
                      <div className="pt-3 border-t">
                        <h4 className="font-semibold mb-3">Interior</h4>
                        <div className="space-y-3">
                          <h5 className="text-sm font-medium">Appliances</h5>
                          <div className="grid grid-cols-2 grid-flow-col grid-rows-4 gap-3">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="appliance_stove"
                                checked={formData.appliance_stove}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, appliance_stove: checked as boolean })
                                }
                              />
                              <Label htmlFor="appliance_stove" className="font-normal cursor-pointer">
                                Stove
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="appliance_oven"
                                checked={formData.appliance_oven}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, appliance_oven: checked as boolean })
                                }
                              />
                              <Label htmlFor="appliance_oven" className="font-normal cursor-pointer">
                                Oven
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="appliance_microwave"
                                checked={formData.appliance_microwave}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, appliance_microwave: checked as boolean })
                                }
                              />
                              <Label htmlFor="appliance_microwave" className="font-normal cursor-pointer">
                                Microwave
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="appliance_dishwasher"
                                checked={formData.appliance_dishwasher}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, appliance_dishwasher: checked as boolean })
                                }
                              />
                              <Label htmlFor="appliance_dishwasher" className="font-normal cursor-pointer">
                                Dishwasher
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="appliance_refrigerator"
                                checked={formData.appliance_refrigerator}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, appliance_refrigerator: checked as boolean })
                                }
                              />
                              <Label htmlFor="appliance_refrigerator" className="font-normal cursor-pointer">
                                Refrigerator
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="appliance_washing_machine"
                                checked={formData.appliance_washing_machine}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, appliance_washing_machine: checked as boolean })
                                }
                              />
                              <Label htmlFor="appliance_washing_machine" className="font-normal cursor-pointer">
                                Washing Machine
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="appliance_clothes_dryer"
                                checked={formData.appliance_clothes_dryer}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, appliance_clothes_dryer: checked as boolean })
                                }
                              />
                              <Label htmlFor="appliance_clothes_dryer" className="font-normal cursor-pointer">
                                Clothes Dryer
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="appliance_central_air"
                                checked={formData.appliance_central_air}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, appliance_central_air: checked as boolean })
                                }
                              />
                              <Label htmlFor="appliance_central_air" className="font-normal cursor-pointer">
                                Central Air
                              </Label>
                          </div>
                        </div>
                        
                        <div className="space-y-3 mt-3">
                          <h5 className="text-sm font-medium">Flooring/Windows</h5>
                          <div className="grid grid-cols-2 grid-flow-col grid-rows-4 gap-3">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="flooring_carpet"
                                checked={formData.flooring_carpet}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, flooring_carpet: checked as boolean })
                                }
                              />
                              <Label htmlFor="flooring_carpet" className="font-normal cursor-pointer">
                                Carpet
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="flooring_hardwood"
                                checked={formData.flooring_hardwood}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, flooring_hardwood: checked as boolean })
                                }
                              />
                              <Label htmlFor="flooring_hardwood" className="font-normal cursor-pointer">
                                Hardwood
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="flooring_vinyl"
                                checked={formData.flooring_vinyl}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, flooring_vinyl: checked as boolean })
                                }
                              />
                              <Label htmlFor="flooring_vinyl" className="font-normal cursor-pointer">
                                Vinyl
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="flooring_other"
                                checked={formData.flooring_other}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, flooring_other: checked as boolean })
                                }
                              />
                              <Label htmlFor="flooring_other" className="font-normal cursor-pointer">
                                Other
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="windows_blinds"
                                checked={formData.windows_blinds}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, windows_blinds: checked as boolean })
                                }
                              />
                              <Label htmlFor="windows_blinds" className="font-normal cursor-pointer">
                                Blinds
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="windows_drapes"
                                checked={formData.windows_drapes}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, windows_drapes: checked as boolean })
                                }
                              />
                              <Label htmlFor="windows_drapes" className="font-normal cursor-pointer">
                                Drapes
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="windows_shades"
                                checked={formData.windows_shades}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, windows_shades: checked as boolean })
                                }
                              />
                              <Label htmlFor="windows_shades" className="font-normal cursor-pointer">
                                Shades
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="windows_none"
                                checked={formData.windows_none}
                                onCheckedChange={(checked) => 
                                  setFormData({ ...formData, windows_none: checked as boolean })
                                }
                              />
                              <Label htmlFor="windows_none" className="font-normal cursor-pointer">
                                None
                              </Label>
                            </div>
                          </div>
                        </div>
                      </div>
                      </div>
                      
                      <div className="pt-3 border-t">
                        <h4 className="font-semibold mb-3">Utilities Included</h4>
                        <div className="grid grid-cols-2 grid-flow-col grid-rows-5 gap-3">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="utility_landscaping"
                              checked={formData.utility_landscaping}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, utility_landscaping: checked as boolean })
                              }
                            />
                            <Label htmlFor="utility_landscaping" className="font-normal cursor-pointer">
                              Landscaping
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="utility_lawn_care"
                              checked={formData.utility_lawn_care}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, utility_lawn_care: checked as boolean })
                              }
                            />
                            <Label htmlFor="utility_lawn_care" className="font-normal cursor-pointer">
                              Lawn Care
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="utility_snow_removal"
                              checked={formData.utility_snow_removal}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, utility_snow_removal: checked as boolean })
                              }
                            />
                            <Label htmlFor="utility_snow_removal" className="font-normal cursor-pointer">
                              Snow Removal
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="utility_electric"
                              checked={formData.utility_electric}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, utility_electric: checked as boolean })
                              }
                            />
                            <Label htmlFor="utility_electric" className="font-normal cursor-pointer">
                              Electric
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="utility_gas"
                              checked={formData.utility_gas}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, utility_gas: checked as boolean })
                              }
                            />
                            <Label htmlFor="utility_gas" className="font-normal cursor-pointer">
                              Gas
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="utility_water"
                              checked={formData.utility_water}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, utility_water: checked as boolean })
                              }
                            />
                            <Label htmlFor="utility_water" className="font-normal cursor-pointer">
                              Water
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="utility_oil"
                              checked={formData.utility_oil}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, utility_oil: checked as boolean })
                              }
                            />
                            <Label htmlFor="utility_oil" className="font-normal cursor-pointer">
                              Oil
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="utility_cable_internet"
                              checked={formData.utility_cable_internet}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, utility_cable_internet: checked as boolean })
                              }
                            />
                            <Label htmlFor="utility_cable_internet" className="font-normal cursor-pointer">
                              Cable/Internet
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="utility_renters_insurance"
                              checked={formData.utility_renters_insurance}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, utility_renters_insurance: checked as boolean })
                              }
                            />
                            <Label htmlFor="utility_renters_insurance" className="font-normal cursor-pointer">
                              Renters Insurance
                            </Label>
                          </div>
                        </div>
                      </div>
                      
                      <div className="pt-3 border-t">
                        <h4 className="font-semibold mb-3">Parking</h4>
                        <div className="grid grid-cols-2 grid-flow-col grid-rows-2 gap-3">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="parking_garage"
                              checked={formData.parking_garage}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, parking_garage: checked as boolean })
                              }
                            />
                            <Label htmlFor="parking_garage" className="font-normal cursor-pointer">
                              Garage
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="parking_carport"
                              checked={formData.parking_carport}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, parking_carport: checked as boolean })
                              }
                            />
                            <Label htmlFor="parking_carport" className="font-normal cursor-pointer">
                              Carport
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="parking_street"
                              checked={formData.parking_street}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, parking_street: checked as boolean })
                              }
                            />
                            <Label htmlFor="parking_street" className="font-normal cursor-pointer">
                              Street Parking
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="parking_reserved_spot"
                              checked={formData.parking_reserved_spot}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, parking_reserved_spot: checked as boolean })
                              }
                            />
                            <Label htmlFor="parking_reserved_spot" className="font-normal cursor-pointer">
                              Reserved Spot
                            </Label>
                          </div>
                        </div>
                      </div>
                      
                      <div className="pt-3 border-t">
                        <h4 className="font-semibold mb-3">Pets</h4>
                        <div className="grid grid-cols-2 grid-flow-col grid-rows-2 gap-3">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="pets_cats_allowed"
                              checked={formData.pets_cats_allowed}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, pets_cats_allowed: checked as boolean })
                              }
                            />
                            <Label htmlFor="pets_cats_allowed" className="font-normal cursor-pointer">
                              Cats Allowed
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="pets_dogs_allowed"
                              checked={formData.pets_dogs_allowed}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, pets_dogs_allowed: checked as boolean })
                              }
                            />
                            <Label htmlFor="pets_dogs_allowed" className="font-normal cursor-pointer">
                              Dogs Allowed
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="pets_other_allowed"
                              checked={formData.pets_other_allowed}
                              onCheckedChange={(checked) => 
                                setFormData({ ...formData, pets_other_allowed: checked as boolean })
                              }
                            />
                            <Label htmlFor="pets_other_allowed" className="font-normal cursor-pointer">
                              Other Allowed
                            </Label>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div>
                            <Label htmlFor="pets_deposit">Pet Deposit</Label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                              <Input
                                id="pets_deposit"
                                type="text"
                                inputMode="decimal"
                                value={formData.pets_deposit || ''}
                                onChange={(e) => setFormData({ ...formData, pets_deposit: parseFloat(e.target.value) || 0 })}
                                className="pl-7"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <Label htmlFor="pets_fee">Pet Fee</Label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                              <Input
                                id="pets_fee"
                                type="text"
                                inputMode="decimal"
                                value={formData.pets_fee || ''}
                                onChange={(e) => setFormData({ ...formData, pets_fee: parseFloat(e.target.value) || 0 })}
                                className="pl-7"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <Label htmlFor="pets_max_dogs">Max Number of Dogs</Label>
                            <Input
                              id="pets_max_dogs"
                              type="number"
                              min="0"
                              value={formData.pets_max_dogs || ''}
                              onChange={(e) => setFormData({ ...formData, pets_max_dogs: e.target.value ? parseInt(e.target.value) : 0 })}
                            />
                          </div>
                          
                          <div>
                            <Label htmlFor="pets_max_cats">Max Number of Cats</Label>
                            <Input
                              id="pets_max_cats"
                              type="number"
                              min="0"
                              value={formData.pets_max_cats || ''}
                              onChange={(e) => setFormData({ ...formData, pets_max_cats: e.target.value ? parseInt(e.target.value) : 0 })}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <Button onClick={handleSubmit} className="w-full">
                    {editingProperty ? "Update Property" : "Add Property"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>My Properties</CardTitle>
              <CardDescription>
                {properties.length} propert{properties.length !== 1 ? "ies" : "y"} in your portfolio
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Beds/Baths</TableHead>
                    <TableHead>Rent</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {properties.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No properties yet. Click "Add Property" to register your first property.
                      </TableCell>
                    </TableRow>
                  ) : (
                    properties.map((property) => (
                      <TableRow key={property.id}>
                        <TableCell className="font-medium">{property.name}</TableCell>
                        <TableCell>{property.city}, {property.state}</TableCell>
                        <TableCell>{property.bedrooms} / {property.bathrooms}</TableCell>
                        <TableCell>${property.rent_amount}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadge(property.status)}>
                            {property.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              setSelectedPropertyForImages(property.id);
                              setShowImagesDialog(true);
                            }}
                            title="Manage images"
                          >
                            <Home className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(property)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(property.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Active Placements
              </CardTitle>
              <CardDescription>
                Current tenant placements across your properties
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Client ID</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {placements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No placements yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    placements.map((placement) => (
                      <TableRow key={placement.id}>
                        <TableCell className="font-medium">{placement.properties?.name}</TableCell>
                        <TableCell className="font-mono text-sm">{placement.client_id.slice(0, 8)}...</TableCell>
                        <TableCell>{new Date(placement.start_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {placement.end_date ? new Date(placement.end_date).toLocaleDateString() : "Ongoing"}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={placement.status}
                            onValueChange={(newStatus) => handlePlacementStatusChange(placement.id, placement.status, newStatus)}
                          >
                            <SelectTrigger className="w-32">
                              <Badge variant={getPlacementStatusBadge(placement.status)}>
                                {getPlacementStatusLabel(placement.status)}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Status change will notify client via email"
                          >
                            <Bell className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <PlacementCalendar />
        </div>
      </main>

      {/* Images Dialog */}
      <Dialog open={showImagesDialog} onOpenChange={setShowImagesDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Property Images</DialogTitle>
            <DialogDescription>
              Upload and manage images for this property
            </DialogDescription>
          </DialogHeader>
          {selectedPropertyForImages && (
            <PropertyImageGallery propertyId={selectedPropertyForImages} />
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default LandlordDashboard;
